/**
 * Enumerations shared by the API, admin app and widget.
 *
 * These mirror the Postgres enum types created in Phase 1 migrations. When you
 * change one here you MUST also write a migration — the DB is the source of
 * truth, this file is the compile-time echo of it.
 */
export const LOCALES = ['en', 'ar'];
/** Text direction per locale. Drives <html dir> and the floor-plan mirroring. */
export const DIRECTION = { en: 'ltr', ar: 'rtl' };
/**
 * Which digits to render. GCC restaurant staff overwhelmingly expect Western
 * digits even when the UI is Arabic, so 'western' is the default — but it is a
 * per-venue setting, not a hardcode.
 */
export const DIGIT_STYLES = ['western', 'arabic'];
export const SEAT_TYPES = [
    'booth',
    'standard',
    'high_top',
    'bar',
    'banquette',
    'cabana',
    'majlis',
];
/**
 * Reservation lifecycle. Ordered roughly by progression, but transitions are
 * validated explicitly in the domain layer — not by array position.
 */
export const RESERVATION_STATUSES = [
    'pending',
    'confirmed',
    'reconfirmed',
    'arrived',
    'seated',
    'dessert',
    'check_dropped',
    'finished',
    'no_show',
    'cancelled_by_guest',
    'cancelled_by_venue',
    'late_cancel',
];
/** Statuses that no longer occupy a table. */
export const TERMINAL_STATUSES = [
    'finished',
    'no_show',
    'cancelled_by_guest',
    'cancelled_by_venue',
    'late_cancel',
];
/** Statuses where the party is physically in the room. */
export const OCCUPYING_STATUSES = [
    'seated',
    'dessert',
    'check_dropped',
];
export const RESERVATION_SOURCES = [
    'widget',
    'whatsapp',
    'phone',
    'walk_in',
    'staff',
    'instagram',
];
export const SPECIAL_DATE_TYPES = [
    'closed',
    'special_hours',
    'blackout',
    'high_demand',
];
export const WAITLIST_STATUSES = [
    'waiting',
    'notified',
    'seated',
    'abandoned',
    'expired',
];
export const MESSAGE_CHANNELS = ['whatsapp', 'sms', 'email'];
export const MESSAGE_STATUSES = ['queued', 'sent', 'delivered', 'read', 'failed'];
/**
 * Staff roles. Permissions live in an explicit matrix in the API
 * (api/src/auth/permissions.ts, Phase 3) — never inferred from role name.
 */
export const ROLES = [
    'owner',
    'group_admin',
    'venue_manager',
    'host',
    'server',
    'marketing',
    'call_centre',
];
/** Availability confidence surfaced to the widget ("only a few tables left"). */
export const SLOT_CONFIDENCE = ['open', 'limited', 'waitlist_only'];
export const ASSIGNED_BY = ['auto', 'manual'];
export const MESSAGE_DIRECTIONS = ['outbound', 'inbound'];
export const DEPOSIT_STATUSES = [
    'pending',
    'authorized',
    'captured',
    'refunded',
    'failed',
    'waived',
];
/** Providers the schema can record. Only one is wired up, in Phase 6. */
export const DEPOSIT_PROVIDERS = ['myfatoorah', 'stripe', 'manual'];
/**
 * Every Postgres enum type and the TypeScript array that must match it.
 * api/tests/schema.test.ts asserts these agree label for label - the DB is the
 * source of truth and this is its compile-time echo, so a drift is a bug in
 * both places at once.
 */
export const PG_ENUM_PARITY = {
    yp_locale: LOCALES,
    yp_digit_style: DIGIT_STYLES,
    yp_seat_type: SEAT_TYPES,
    yp_reservation_status: RESERVATION_STATUSES,
    yp_reservation_source: RESERVATION_SOURCES,
    yp_assigned_by: ASSIGNED_BY,
    yp_special_date_type: SPECIAL_DATE_TYPES,
    yp_waitlist_status: WAITLIST_STATUSES,
    yp_message_channel: MESSAGE_CHANNELS,
    yp_message_direction: MESSAGE_DIRECTIONS,
    yp_message_status: MESSAGE_STATUSES,
    yp_role: ROLES,
    yp_deposit_status: DEPOSIT_STATUSES,
    yp_deposit_provider: DEPOSIT_PROVIDERS,
};
//# sourceMappingURL=enums.js.map